'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_OPEN_DELAY_MS = 600;
const DEFAULT_CLOSE_DELAY_MS = 400;

type HoverPopoverOptions = {
  /**
   * `id` passed to the `Popover`. The surface portals to <body>, so pointer
   * position is resolved by looking it up rather than by DOM containment.
   */
  surfaceId: string;
  openDelayMs?: number;
  closeDelayMs?: number;
};

/**
 * Hover-to-open behaviour for `Popover`, which is click-only by design.
 *
 * `TpTooltip` opens on hover but sets `pointer-events: none`, so it cannot hold
 * a button or link. `Popover` is interactive but click-triggered. This bridges
 * the two for logo/brand affordances that reveal a clickable CTA on hover: the
 * close is delayed so the pointer can cross the gap from trigger to surface,
 * and the delay is cancelled while the pointer rests on either one.
 *
 * Click still toggles, so the affordance works without a pointer.
 */
export function useHoverPopover({
  surfaceId,
  openDelayMs = DEFAULT_OPEN_DELAY_MS,
  closeDelayMs = DEFAULT_CLOSE_DELAY_MS,
}: HoverPopoverOptions) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelOpen = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  }, []);

  const keepOpen = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      cancelOpen();
      keepOpen();
    },
    [cancelOpen, keepOpen],
  );

  const scheduleOpen = useCallback(() => {
    keepOpen();
    cancelOpen();
    showTimerRef.current = setTimeout(() => setOpen(true), openDelayMs);
  }, [cancelOpen, keepOpen, openDelayMs]);

  const scheduleClose = useCallback(() => {
    cancelOpen();
    // Leaving and re-entering must not restart an already-running close.
    if (hideTimerRef.current) return;
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null;
      setOpen(false);
    }, closeDelayMs);
  }, [cancelOpen, closeDelayMs]);

  useEffect(() => {
    if (!open) return;
    const onPointerOver = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      const surface = document.getElementById(surfaceId);
      if (surface?.contains(target) || triggerRef.current?.contains(target)) {
        keepOpen();
        return;
      }
      scheduleClose();
    };
    const onDocumentLeave = () => scheduleClose();
    document.addEventListener('pointerover', onPointerOver);
    document.addEventListener('pointerleave', onDocumentLeave);
    return () => {
      document.removeEventListener('pointerover', onPointerOver);
      document.removeEventListener('pointerleave', onDocumentLeave);
    };
  }, [open, surfaceId, keepOpen, scheduleClose]);

  const setOpenGuarded = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      if (typeof value === 'function') {
        setOpen((prev) => {
          const next = value(prev);
          if (next) {
            keepOpen();
          } else {
            cancelOpen();
          }
          return next;
        });
      } else {
        if (value) {
          keepOpen();
        } else {
          cancelOpen();
        }
        setOpen(value);
      }
    },
    [cancelOpen, keepOpen],
  );

  const toggle = useCallback(() => {
    setOpen((current) => {
      if (current) {
        cancelOpen();
        return false;
      }
      keepOpen();
      cancelOpen();
      return true;
    });
  }, [cancelOpen, keepOpen]);

  return {
    open,
    setOpen: setOpenGuarded,
    triggerRef,
    /** Spread onto the trigger button, alongside `ref={triggerRef}`. */
    triggerProps: {
      onPointerEnter: scheduleOpen,
      onPointerLeave: scheduleClose,
      onClick: toggle,
    },
  };
}
